/**
 * useHostelRooms Hook
 * Manages hostel room data fetching and state
 */

import { useState, useEffect } from 'react';
import { hostelRoomService } from '../domain/services/HostelRoomService.js';

export const useHostelRooms = (hostelId) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomLayout, setRoomLayout] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(false);

  // Fetch rooms on mount or when hostelId changes
  useEffect(() => {
    if (!hostelId) {
      return;
    }

    const fetchRooms = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await hostelRoomService.getRooms(hostelId);
        
        // Handle different response formats
        let roomsArray = [];
        if (Array.isArray(data)) {
          roomsArray = data;
        } else if (data?.result?.items && Array.isArray(data.result.items)) {
          // API returns { result: { items: [...] } }
          roomsArray = data.result.items;
        } else if (data?.data && Array.isArray(data.data)) {
          roomsArray = data.data;
        } else if (data?.rooms && Array.isArray(data.rooms)) {
          roomsArray = data.rooms;
        }
        
        // Transform rooms to match expected format
        const transformedRooms = roomsArray.map(room => ({
          id: room.id,
          name: `Room ${room.roomNumber}`,
          roomNumber: room.roomNumber,
          floor: room.floor,
          roomType: room.gender || 'Standard',
          capacity: room.bedCount || 1,
          beds: room.bedCount || 1,
          price: room.monthlyRate || 0,
          available: room.availableBeds > 0,
          availableBeds: room.availableBeds,
          occupiedBeds: room.occupiedBeds,
          image: room.images?.[0] || null,
          images: room.images || [],
          description: room.description || '',
          amenities: [],
          layout: room.layout || null,
          beds: room.beds || [],
          status: room.status,
        }));
        
        setRooms(transformedRooms);
      } catch (err) {
        const errorMsg = err.message || 'Failed to fetch rooms';
        setError(errorMsg);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRooms();
  }, [hostelId]);

  // Set room layout from selected room data
  useEffect(() => {
    if (!selectedRoom?.layout) {
      setRoomLayout(null);
      return;
    }

    // Room layout is already in the room data
    setRoomLayout({
      layoutImage: null, // API doesn't provide separate layout image
      furniture: selectedRoom.layout.elements?.map(el => el.type) || [],
      dimensions: selectedRoom.layout.dimensions ? 
        `${selectedRoom.layout.dimensions.width}x${selectedRoom.layout.dimensions.height}` : null,
      elements: selectedRoom.layout.elements || [],
      beds: selectedRoom.beds || [],
    });
  }, [selectedRoom]);

  return {
    rooms,
    loading,
    error,
    selectedRoom,
    setSelectedRoom,
    roomLayout,
    layoutLoading: false,
  };
};
